import {ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {BaseComponent, GLOBAL, IdTextValueModel, LeModalService} from "common-shared";
import {GlobalSearchResultGroupModel, GlobalSearchResultModel, GlobalSearchService} from "../global-search.service";
import {BehaviorSubject, debounceTime} from "rxjs";
import {filter, map, take, takeUntil} from "rxjs/operators";
import {EntityTypes, PropertyEditModalComponent, StateService} from "web-shared";
import {Router} from "@angular/router";
import {GroupByPipe} from "ngx-pipes";
import {ManufacturingItemEditModalComponent} from "../../../manufacturing/manufacturing-item/manufacturing-item-edit-modal/manufacturing-item-edit-modal.component";
import {ManufacturingItemGroupEditModalComponent} from "../../../manufacturing/manufactured-item-groups/manufacturing-item-group-edit-modal/manufacturing-item-group-edit-modal.component";
import {ResourceEditModalComponent} from "../../../manufacturing/resources/resource-edit-modal/resource-edit-modal.component";
import {NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {ObjectUtils} from "primeng/utils";
import {plainToInstance} from "class-transformer";

@Component({
  selector: 'new-global-search',
  templateUrl: './new-global-search.component.html',
  styles: [
    `.searchbox {
      background-color: #4c4c4c;
      color: rgb(150, 150, 150);
    }`
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NewGlobalSearchComponent extends BaseComponent implements OnInit {
  @ViewChild('searchModal') searchModal: TemplateRef<any>;
  searchString$ = new BehaviorSubject<string>(null);
  lastSearch: string;
  groupedResults: GlobalSearchResultGroupModel[] = [];
  entityTypes = EntityTypes;
  favourites: GlobalSearchResultModel[] = []
  favouritesType = 999;
  modalRef: NgbModalRef;
  activeGroup: number;
  activeRow: number;
  activeBtn: number;
  isOpen = false;

  constructor(private searchService: GlobalSearchService, private modalService: LeModalService, private router: Router, private groupByPipe: GroupByPipe, private stateService: StateService, private changeDetector: ChangeDetectorRef) {
    super();
  }

  ngOnInit() {
    this.searchString$.pipe(filter(value => value != null), debounceTime(300), takeUntil(this.componentDestroyed)).subscribe(searchString => {
      this.searchService.get(searchString).pipe(map(value => {
        this.lastSearch = searchString;
        this.groupedResults = [];
        const groupObject = this.groupByPipe.transform(value, 'Type');
        if (groupObject[EntityTypes.ItemGroup.toString()]) {
          this.groupedResults.push(this.getResultGroup(EntityTypes.ItemGroup, groupObject[EntityTypes.ItemGroup.toString()]));
        }
        if (groupObject[EntityTypes.Item.toString()]) {
          this.groupedResults.push(this.getResultGroup(EntityTypes.Item, groupObject[EntityTypes.Item.toString()]));
        }
        if (groupObject[EntityTypes.Resource.toString()]) {
          this.groupedResults.push(this.getResultGroup(EntityTypes.Resource, groupObject[EntityTypes.Resource.toString()]));
        }
        if (groupObject[EntityTypes.Property.toString()]) {
          this.groupedResults.push(this.getResultGroup(EntityTypes.Property, groupObject[EntityTypes.Property.toString()]));
        }
        this.changeDetector.markForCheck();
      }), takeUntil(this.componentDestroyed)).subscribe();
    });

    this.prepareFavourites();
  }

  openSearch(templateRef: TemplateRef<any>) {
    this.removeResultFocus();
    this.isOpen = true;
    this.modalRef = this.modalService.open(templateRef, {size: 'md', backdrop: true, keyboard: true, scrollable: true});
    this.modalRef.hidden.pipe(take(1)).subscribe(() => this.isOpen = false);
  }

  @HostListener('document:keydown.alt.q')
  openSearchWithKeyboard() {
    if (!this.isOpen) {
      this.scrollIntoView('globalSearch');
      this.openSearch(this.searchModal);
    }
  }

  private scrollIntoView(id: string) {
    setTimeout(() => {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({behavior: 'smooth', block: "center"});
      }
    });
  }

  onkeydown(event: KeyboardEvent) {
    switch (event.code) {
      case 'ArrowUp':
        this.onArrowUp();
        break
      case 'ArrowDown':
        this.onArrowDown();
        break
      case 'PageUp':
        this.onPageUp();
        break;
      case 'PageDown':
        this.onPageDown();
        break;
      case 'Enter':
      case 'NumpadEnter':
        this.onEnter();
        break;
      case 'Tab':
        this.onTab();
        break;
      default:
        if (ObjectUtils.isPrintableCharacter(event.key)) {
          this.removeResultFocus()
        }
        break;
    }
  }

  private onArrowUp() {
    if (this.activeGroup == null && this.activeRow == null) {
      this.activeGroup = this.groupedResults.length - 1;
      if (!this.groupedResults[this.activeGroup].IsCollapsed) {
        this.activeRow = this.groupedResults[this.activeGroup].Results.length - 1;
      }
    } else if (this.activeGroup != null && this.activeRow == null) {
      if (this.activeGroup > 0) {
        this.activeGroup--;
        if (!this.groupedResults[this.activeGroup].IsCollapsed) {
          this.activeRow = this.groupedResults[this.activeGroup].Results.length - 1;
        }
      } else {
        this.activeGroup = this.groupedResults.length - 1;
        if (!this.groupedResults[this.activeGroup].IsCollapsed) {
          this.activeRow = this.groupedResults[this.activeGroup].Results.length - 1;
        }
      }
    } else if (this.activeGroup != null && this.activeRow != null) {
      if (this.activeRow > 0) {
        this.activeRow--;
      } else {
        this.activeRow = null;
      }
    }
    this.activeBtn = null;
    this.scrollResultIntoView();
  }

  private onArrowDown() {
    if (this.activeGroup == null && this.activeRow == null) {
      this.activeGroup = 0;
    } else if (this.activeGroup != null && this.activeRow == null) {
      if (this.groupedResults[this.activeGroup].IsCollapsed) {
        this.focusNextGroup();
      } else {
        this.activeRow = 0;
      }
    } else if (this.activeGroup != null && this.activeRow != null) {
      if (this.groupedResults[this.activeGroup].Length > this.activeRow + 1) {
        this.activeRow++;
      } else {
        this.focusNextGroup();
      }
    }
    this.activeBtn = null;
    this.scrollResultIntoView();
  }

  onPageUp() {
    if (this.activeGroup != null && this.activeRow == null) {
      this.activeGroup = 0;
    } else if (this.activeGroup != null && this.activeRow != null) {
      if (this.activeRow == 0) {
        if (this.activeGroup > 0) {
          this.activeGroup--;
        }
        this.activeRow = null;
      } else {
        this.activeRow = 0;
      }
    }
    this.scrollResultIntoView();
  }

  onPageDown() {
    if (this.activeGroup != null && this.activeRow == null) {
      this.activeGroup = this.groupedResults.length - 1;
    } else if (this.activeGroup != null && this.activeRow != null) {
      this.activeRow = this.groupedResults[this.activeGroup].Results.length - 1;
    }
    this.scrollResultIntoView();
  }

  private onTab() {
    if (this.activeGroup != null && this.activeRow != null) {
      if (this.activeBtn == null) {
        this.activeBtn = 1;
      } else if (this.activeBtn == 1) {
        this.activeBtn++;
      } else if (this.activeBtn == 2) {
        this.activeBtn = null;
      }
    }
  }

  private onEnter() {
    if (this.activeGroup != null && this.activeRow != null) {
      let target: 'modal' | 'blank' | 'default' = 'default';
      if (this.activeBtn === 1) {
        target = 'blank'
      }
      if (this.activeBtn === 2) {
        target = 'modal'
      }
      setTimeout(() => this.open(this.groupedResults[this.activeGroup].Results[this.activeRow], target));
    } else if (this.activeGroup != null && this.activeRow == null) {
      this.groupedResults[this.activeGroup].IsCollapsed = !this.groupedResults[this.activeGroup].IsCollapsed;
    }
  }

  private focusNextGroup() {
    this.activeRow = null;
    if (this.groupedResults.length > this.activeGroup + 1) {
      this.activeGroup++;
    } else {
      this.activeGroup = 0;
    }
  }

  removeResultFocus() {
    this.activeGroup = null;
    this.activeRow = null;
    this.activeBtn = null;
  }

  onMouseEnter(groupIndex, rowIndex) {
    this.activeGroup = groupIndex;
    this.activeRow = rowIndex;
    this.activeBtn = null;
  }

  scrollResultIntoView() {
    if (this.activeGroup != null && this.activeRow != null) {
      this.scrollIntoView(`row-${this.activeGroup}-${this.activeRow}`);
    }
    if (this.activeGroup != null && this.activeRow == null) {
      this.scrollIntoView(`group-${this.activeGroup}`);
    }
  }

  search(term: string) {
    if (term?.length > 1) {
      this.searchString$.next(term);
    } else {
      this.setFavouritesAsResults();
    }
  }

  setFavouritesAsResults() {
    this.groupedResults = this.favourites.length ? [this.getResultGroup(this.favouritesType, this.favourites)] : [];
  }

  getResultGroup(groupCode: number, children: any[]): GlobalSearchResultGroupModel {
    let name: string;
    let cssClass: string;
    switch (groupCode) {
      case EntityTypes.Item: {
        name = 'Items';
        cssClass = 'bg-secondary text-light'
        break;
      }
      case EntityTypes.ItemGroup: {
        name = 'Item templates';
        cssClass = 'bg-primary text-light'
        break;
      }
      case EntityTypes.Resource: {
        name = 'Resources';
        cssClass = 'bg-info text-light'
        break;
      }
      case EntityTypes.Property: {
        name = 'Specs';
        cssClass = 'bg-indigo-400 text-light'
        break;
      }
      case this.favouritesType: {
        name = 'Favourites';
        cssClass = 'bg-warning text-light'
        break;
      }
      default:
        name = 'Other';
        cssClass = 'bg-default'
        break;
    }
    return {Name: name, Type: groupCode, Results: children, Length: children.length, CssClass: cssClass, IsCollapsed: false};
  };

  open(item: GlobalSearchResultModel, target: 'modal' | 'blank' | 'default') {
    this.modalRef?.close();
    if (item.Type === this.favouritesType) {
      this.openFavourite(item, target as 'blank' | 'default');
      return;
    }

    let namespace = '';
    let area = 'manufacturing';
    switch (item.Type) {
      case EntityTypes.Item: {
        namespace = 'item';
        break;
      }
      case EntityTypes.ItemGroup: {
        namespace = 'item-template';
        break;
      }
      case EntityTypes.Resource: {
        namespace = 'resource';
        break;
      }
      case EntityTypes.Property: {
        namespace = 'property';
        area = 'common';
        break
      }
    }

    if (namespace) {
      const commands = [area, namespace, 'edit', item.Id];

      if (target === 'default') {
        this.router.navigate(commands).catch(reason => console.error(reason));
      } else if (target === 'blank') {
        const url = this.router.serializeUrl(this.router.createUrlTree(commands));
        window.open(url, '_blank');
      } else if (target === 'modal') {
        this.openModal(item);
      }
    }
  }

  openModal(item: GlobalSearchResultModel) {
    switch (item.Type) {
      case EntityTypes.Item: {
        this.modalService.show(ManufacturingItemEditModalComponent, {initialState: {entityId: item.Id}, class: 'modal-xl'});
        break;
      }
      case EntityTypes.ItemGroup: {
        this.modalService.show(ManufacturingItemGroupEditModalComponent, {initialState: {entityId: item.Id}, class: 'modal-xl'});
        break;
      }
      case EntityTypes.Resource: {
        this.modalService.show(ResourceEditModalComponent, {initialState: {entityId: item.Id}, class: 'modal-xl'});
        break;
      }
      case EntityTypes.Property: {
        this.modalService.show(PropertyEditModalComponent, {initialState: {propertyId: item.Id}});
        break;
      }
    }
  }

  openFavourite(favourite: GlobalSearchResultModel, target: 'blank' | 'default') {
    if (target === 'default') {
      if (favourite.Value.startsWith('http')) {
        window.open(favourite.Value, '_self');
      } else {
        this.router.navigate([favourite.Value]).catch(reason => console.error(reason));
      }
    } else if (target === 'blank') {
      window.open(favourite.Value, '_blank');
    }
  }

  private prepareFavourites() {
    this.stateService.currentUser$.pipe(takeUntil(this.componentDestroyed)).subscribe(user => {
      if (user?.Specs) {
        this.favourites = [];
        const favouritesSpec = user.Specs.find(spec => spec.PropertyId === GLOBAL.Properties.Favourites);
        if (favouritesSpec?.Value) {
          const favourites = plainToInstance(IdTextValueModel, <string[]>JSON.parse(favouritesSpec.Value));
          if (favourites.length) {
            favourites.forEach(favourite => {
              this.favourites.push(new GlobalSearchResultModel({Id: favourite.Id, Name: favourite.Text, Value: favourite.Value, Type: this.favouritesType}));
            });
            if (!this.groupedResults.length) {
              this.setFavouritesAsResults();
            }
          }
        }
      }
    });
  }
}
